import { useState } from 'react';
import type { AuthResult } from '@aiscoreboard/contracts';
import { SignInPanel } from '../components/SignInPanel';
import { claimPairing } from '../lib/accounts';
import { useT } from '../lib/i18n';

interface Props {
  code: string;
  onJoined: (auth: AuthResult) => void;
  onCancel: () => void;
}

/**
 * What a phone sees after scanning a scoreboard's pairing QR code.
 *
 * The account is created here, on the player's own device, where a keyboard is
 * comfortable and nobody is watching them type a password. The scoreboard is
 * polling; the moment this claim succeeds the seat fills in over there.
 */
export function JoinScreen({ code, onJoined, onCancel }: Props) {
  const t = useT();
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const claim = (auth: AuthResult): void => {
    setClaiming(true);
    setError(null);
    claimPairing(auth.token, code)
      .then(() => onJoined(auth))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : t('join.failed'));
        setClaiming(false);
      });
  };

  if (claiming) {
    return (
      <main className="grid h-full place-items-center p-4">
        <p role="status" className="text-white/60">{t('common.loading')}</p>
      </main>
    );
  }

  return (
    <main className="grid h-full place-items-center p-4">
      <div className="flex w-full max-w-md flex-col gap-3">
        <div className="panel text-center">
          <h2 className="text-lg font-bold">{t('join.title')}</h2>
          <p className="mt-1 min-h-[2.5rem] text-sm text-white/60">{t('join.subtitle')}</p>
        </div>

        {error && <p className="panel text-center text-sm text-ball-red">{error}</p>}

        {/* No pairing tab: this device *is* the phone. */}
        <SignInPanel
          title={t('join.account')}
          onDone={claim}
          onCancel={onCancel}
        />
      </div>
    </main>
  );
}
